import React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  IMobileFlow,
  isMobileOffer,
  useOffer,
  useOfferMode,
} from "@nju/data-access-portal-offers";
import { IMobileOrchestrator } from "@nju/data-access-portal-orchestrator";
import { portalRoutes } from "@nju/routes";
import { CircleLoader, Panel, Separator, Text } from "@nju/ui";
import { getMobileStatusData } from "./getMobileStatusData";
import { mobileStatusMachine } from "./mobileStatusBanner.machine";
import { MobileStatusBannerDetails } from "./MobileStatusBannerDetails";
import { Buttons } from "./partials/Buttons";

export interface IMobileStatusBannerProps {
  orchestrator: IMobileOrchestrator;
  flow: IMobileFlow | undefined;
}

export function MobileStatusBanner({
  orchestrator,
  flow,
}: IMobileStatusBannerProps) {
  const mode = useOfferMode();
  const { reload, push } = useRouter();
  const {
    id,
    state,
    offerId,
    shippingStatus,
    contactNumber,
    delivery,
    deliveryMethodId,
    processId,
    verifiedMsisdn,
    isUserVerified,
    identityMethodId,
    paymentStatus,
    paymentErrorCategory,
    cancellationReason,
    orderSimNumber,
    mnp,
    userId,
  } = orchestrator;

  const [current, send] = useMachine(mobileStatusMachine, {
    actions: {
      onActivateSuccess: () => {
        reload();
      },
      onDWInsufficientFunds: () => {
        push(
          portalRoutes.mojeKonto.niewystarczajacaKwotaNaKoncie.get({ mode }),
        );
      },
      onRetryPaymentSuccess: () => {
        reload();
      },
      onCancelOrderSuccess: () => {
        push(portalRoutes.mojeKonto.index.get({ mode }));
      },
    },
    context: {
      orchestratorId: id,
    },
  });

  const { matches } = current;
  const isLoading = current.matches("retryPayment");
  const offer = useOffer(offerId);

  const handleCancelOrder = React.useCallback(() => {
    send({ type: "CANCEL_ORDER" });
  }, [send]);

  const handleActivateOrder = React.useCallback(() => {
    send({ type: "ACTIVATE_ORDER" });
  }, [send]);

  const refresh = React.useCallback(() => {
    reload();
  }, [reload]);

  const retryPayment = React.useCallback(() => {
    send({ type: "RETRY_PAYMENT" });
  }, [send]);

  const panelProps = React.useMemo(() => {
    return getMobileStatusData({
      paymentErrorCategory,
      state,
      shippingStatus,
      processId,
      isUserVerified,
      identityMethodId,
      orderSimNumber,
      mnp,
    });
  }, [
    paymentErrorCategory,
    state,
    shippingStatus,
    processId,
    isUserVerified,
    identityMethodId,
    orderSimNumber,
    mnp,
  ]);

  React.useEffect(() => {
    if (matches("error")) {
      push(portalRoutes.mojeKonto.bladPlatnosci.get({ mode, userId }));

      return;
    }
  }, [matches, mode, push, userId]);

  if (!panelProps && state === "COMPLETED") {
    return (
      <section
        className="opacity-0 flex items-center justify-center animate-opacity delay-300 duration-1000 pb-8"
        style={{ animationFillMode: "forwards" }}
      >
        <CircleLoader />
      </section>
    );
  }

  if (!panelProps) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const { text, title, iconName, statusDetailsText, statusDetailsIcon } =
    panelProps;

  return (
    <Panel
      iconName={iconName}
      isFoldable={!!offer}
      isLoading={isLoading}
      testId="mv-order-status-banner"
      title={title}
    >
      {({ isExpanded }) => {
        return isExpanded ? (
          <>
            <Text
              size={{
                "@onlyMobile": "m",
                "@fromTablet": "l",
              }}
            >
              {text}
            </Text>
            {isMobileOffer(offer) && (
              <MobileStatusBannerDetails
                clientNumber={verifiedMsisdn}
                contactNumber={contactNumber}
                deliveryAddress={delivery}
                deliveryMethodId={deliveryMethodId}
                iconName={iconName}
                note={delivery?.note}
                offer={offer}
                orchestratorState={state}
                status={title}
                statusIcon={statusDetailsIcon}
                statusText={statusDetailsText}
                transferDate={mnp?.transferDate}
              />
            )}
            <Separator size="xs" tone="invisible" />
            <Buttons
              activateOrder={handleActivateOrder}
              cancelOrder={handleCancelOrder}
              cancellationReason={cancellationReason}
              flow={flow}
              isActivateOrderLoading={matches("activate")}
              isCancelOrderLoading={matches("cancel")}
              orchestrator={orchestrator}
              paymentErrorCategory={paymentErrorCategory}
              paymentStatus={paymentStatus}
              refreshCallback={refresh}
              retryPaymentCallback={retryPayment}
              shippingStatus={shippingStatus}
              userId={userId}
            />
          </>
        ) : (
          <>
            <Text
              size={{
                "@onlyMobile": "m",
                "@fromTablet": "l",
              }}
            >
              {text}
            </Text>
            <Buttons
              activateOrder={handleActivateOrder}
              cancelOrder={handleCancelOrder}
              cancellationReason={cancellationReason}
              flow={flow}
              isActivateOrderLoading={matches("activate")}
              isCancelOrderLoading={matches("cancel")}
              orchestrator={orchestrator}
              paymentErrorCategory={paymentErrorCategory}
              paymentStatus={paymentStatus}
              refreshCallback={refresh}
              retryPaymentCallback={retryPayment}
              shippingStatus={shippingStatus}
              userId={userId}
            />
          </>
        );
      }}
    </Panel>
  );
}
